/**
 * The built-in preview (docs/refactor/11-preview.md): when a turn or ▶ Executar brings something up on
 * this machine (`DEVPULSE-OPEN: http://localhost:…`), the desktop app shows it in the prompt box's
 * Preview tab, beside the conversation, instead of throwing it to the browser.
 *
 * Only addresses on this machine get here (lib/localRunner.ts `localAddressToOpen` already refuses
 * the rest). The page is another origin: the app can show it, reload it and resize it, but not read
 * it — so "Mostrar ao agente" sends the agent the address, never a capture.
 */

import { useSyncExternalStore } from 'react'

export const PREVIEW_EVENT = 'devpulse:preview'

let current: string | null = null
const listeners = new Set<() => void>()

/** Shows `url` in the Preview tab (and switches to it). */
export function showPreview(url: string): void {
  current = url
  for (const listener of listeners) listener()
  window.dispatchEvent(new CustomEvent(PREVIEW_EVENT, { detail: url }))
}

export function closePreview(): void {
  current = null
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** The address in the preview, or null. */
export function usePreview(): string | null {
  return useSyncExternalStore(subscribe, () => current, () => null)
}

/** The sizes the preview can take: a phone, a tablet, or the whole pane. */
export const PREVIEW_SIZES = { phone: 390, tablet: 768, full: null } as const
export type PreviewSize = keyof typeof PREVIEW_SIZES

/** What "Mostrar ao agente" puts in the prompt box: the address and how it was being looked at. */
export function askAboutPreview(url: string, size: PreviewSize, locale: string): string {
  const width = PREVIEW_SIZES[size]
  const how = width ? (locale.startsWith('pt') ? ` numa tela de ${width} px de largura` : ` at ${width} px wide`) : ''
  return locale.startsWith('pt')
    ? `Abra ${url}${how} e veja o que está errado: `
    : `Open ${url}${how} and see what is wrong: `
}
